import { CreatePreparationLotDto, CreateQcTestResultDto, QualityControlData, GeneratorElutionDto, PreparationLotDto, QcTestResultDto, StartPreparationLotStepTimerDto, UpdatePreparationLotDto } from './dtos'
import { BaseCrudService } from './BaseCrudService'
import { axios, PaginatedQueryBase } from './api'

export type PreparationLotsFilters = PaginatedQueryBase & { sourceElutionId?: string }

const endpoint = 'preparation-lot'
const baseUrl = process.env.REACT_APP_RADIOPHARMACY_API_URL

class PreparationLotService extends BaseCrudService<
  PreparationLotDto,
  CreatePreparationLotDto,
  UpdatePreparationLotDto,
  PreparationLotsFilters
> {
  constructor() {
    super(endpoint, baseUrl)
  }

  getAvailableElutions(compoundId: string): Promise<GeneratorElutionDto[]> {
    return new Promise((resolve, reject) => {
      const configs = this.buildConfig('get', 'generator-elution/available', undefined, { compoundId })
      axios(configs, resolve, reject)
    })
  }

  getCurrentActivities(ids: string[]): Promise<Record<string, number | null>> {
    return new Promise((resolve, reject) => {
      const configs = this.buildConfig('get', `${this.endpoint}/current-activity`, undefined, { ids: ids.join(',') })
      axios(configs, resolve, reject)
    })
  }

  startStepTimer(preparationLotId: string, dto: StartPreparationLotStepTimerDto): Promise<PreparationLotDto> {
    return new Promise((resolve, reject) => {
      const configs = this.buildConfig('post', `${this.endpoint}/${preparationLotId}/step-timer`, dto)
      axios(configs, resolve, reject)
    })
  }

  cancelStepTimer(preparationLotId: string, timerId: string): Promise<PreparationLotDto> {
    return new Promise((resolve, reject) => {
      const configs = this.buildConfig('delete', `${this.endpoint}/${preparationLotId}/step-timer/${timerId}`)
      axios(configs, resolve, reject)
    })
  }

  completeStepTimer(preparationLotId: string, timerId: string): Promise<PreparationLotDto> {
    return new Promise((resolve, reject) => {
      const configs = this.buildConfig('patch', `${this.endpoint}/${preparationLotId}/step-timer/${timerId}/complete`)
      axios(configs, resolve, reject)
    })
  }

  getQualityControls(id: string): Promise<QualityControlData | null> {
    return new Promise((resolve, reject) => {
      const configs = this.buildConfig('get', `${this.endpoint}/${id}/quality-controls`)
      axios(configs, resolve, reject)
    })
  }

  completeQualityControl(id: string, completedByMemberId: number): Promise<void> {
    return new Promise((resolve, reject) => {
      const configs = this.buildConfig('patch', `${this.endpoint}/${id}/quality-control/complete`, { completedByMemberId })
      axios(configs, resolve, reject)
    })
  }

  recordQcTestResult(id: string, dto: CreateQcTestResultDto, file?: File): Promise<QcTestResultDto> {
    return new Promise((resolve, reject) => {
      const formData = new FormData()
      formData.append('payload', JSON.stringify({ ...dto, entityType: 'preparationLot', entityId: id }))
      if (file) {
        formData.append('file', file)
      }
      const configs = this.buildConfig('post', 'qc-test-result', formData)
      axios(configs, resolve, reject)
    })
  }

  subscribeToEvents(onUpdated: (lot: PreparationLotDto) => void): () => void {
    const eventSource = new EventSource(`${baseUrl}/${endpoint}/events`)
    eventSource.addEventListener('preparationLot.updated', (messageEvent) => {
      const lot: PreparationLotDto = JSON.parse((messageEvent as MessageEvent).data)
      onUpdated(lot)
    })
    eventSource.onerror = () => console.error(`SSE ${endpoint} connection error`)
    return () => eventSource.close()
  }
}

export const preparationLotService = new PreparationLotService()
